import type {FC} from 'react';

import styled, {css} from 'styled-components';

import type {WithThemeProp} from '../../../styles';
import type {StyledProps} from './types';

import {getColor, square} from '../../../styles';
import {View} from '../../View';

type IconStyledProps = Pick<
  StyledProps,
  'accentColor' | 'disabled' | 'isPressed' | 'scheme' | 'size' | 'variant'
> & {
  icon: FC;
};

const getSchemeColor =
  (
    scheme: IconStyledProps['scheme'],
    accentColor: IconStyledProps['accentColor'],
  ) =>
  <ComponentProps extends WithThemeProp>(p: ComponentProps) => {
    if (accentColor) {
      return getColor(accentColor)(p);
    }

    switch (scheme) {
      case 'attention':
        return getColor('icon.red')(p);
      case 'primary':
      case 'secondary':
        return getColor(`icon.${scheme}`)(p);
      default:
        return getColor('icon.gray')(p);
    }
  };

const getBaseFillColor = (p: IconStyledProps & WithThemeProp) => {
  switch (p.variant) {
    case 'primary':
      return getColor('icon.white')(p);
    default:
      return getSchemeColor(p.scheme, p.accentColor)(p);
  }
};

const getFillColor = (p: IconStyledProps & WithThemeProp) => {
  if (p.disabled) {
    if (p.variant === 'primary') {
      return getColor('icon.white')(p);
    }

    return getColor('icon.gray')(p);
  }

  if (p.isPressed) {
    return getHoverFillColor(p);
  }

  return getBaseFillColor(p);
};

const getHoverFillColor = (p: IconStyledProps & WithThemeProp) => {
  // this condition is for web
  if (p.disabled) {
    return undefined;
  }

  switch (p.variant) {
    case 'outlined':
      return getColor('icon.white')(p);
    default:
      return getBaseFillColor(p);
  }
};

export const hoverFill = css<IconStyledProps>`
  fill: ${getHoverFillColor};
`;

export const getIconSize = (size: IconStyledProps['size']) => {
  switch (size) {
    case 'la':
      return 24;
    case 'me':
      return 20;
    default:
      return 16;
  }
};

export const ButtonIcon = styled(({icon: Icon, ...rest}) => (
  <View nativeProps={{pointerEvents: 'none'}}>
    <Icon {...rest} />
  </View>
)).attrs((p: IconStyledProps & WithThemeProp) => ({
  fill: getFillColor(p),
}))<IconStyledProps>`
  ${p => {
    return css`
      ${square(getIconSize(p.size))};
    `;
  }}
`;
